import { Coordinate } from '../types';

const LEG_FRACTION_CAP = 0.35;
const ARC_LEG_FRACTION_CAP = 0.9;

export function calculateAngle3(
  vertex: Coordinate,
  P2: Coordinate,
  P3: Coordinate
): number | null {
  const V1x = P2.x - vertex.x;
  const V1y = P2.y - vertex.y;
  const V2x = P3.x - vertex.x;
  const V2y = P3.y - vertex.y;

  const mag1 = Math.hypot(V1x, V1y);
  const mag2 = Math.hypot(V2x, V2y);

  if (mag1 === 0 || mag2 === 0) return null;

  const dot = V1x * V2x + V1y * V2y;
  const cross = Math.abs(V1x * V2y - V1y * V2x);

  return Math.atan2(cross, dot) * (180 / Math.PI);
}

export const computeTriangleVertices = (
  pointRatio: number,
  width: number,
  height: number
): {
  apex: Coordinate;
  baseLeft: Coordinate;
  baseRight: Coordinate;
} => {
  const clampedRatio = Math.max(0, Math.min(1, pointRatio));
  return {
    apex: { x: clampedRatio * width, y: 0 },
    baseLeft: { x: 0, y: height },
    baseRight: { x: width, y: height },
  };
};

export const computeTriangleAngles = (vertices: {
  apex: Coordinate;
  baseLeft: Coordinate;
  baseRight: Coordinate;
}): {
  apex: number;
  baseLeft: number;
  baseRight: number;
} | null => {
  const { apex, baseLeft, baseRight } = vertices;

  const apexAngle = calculateAngle3(apex, baseLeft, baseRight);
  const baseLeftAngle = calculateAngle3(baseLeft, apex, baseRight);
  const baseRightAngle = calculateAngle3(baseRight, apex, baseLeft);

  if (apexAngle === null || baseLeftAngle === null || baseRightAngle === null) {
    return null;
  }

  return {
    apex: apexAngle,
    baseLeft: baseLeftAngle,
    baseRight: baseRightAngle,
  };
};

export const cornerArcPath = (
  vertex: Coordinate,
  a: Coordinate,
  b: Coordinate,
  radius: number
): string | null => {
  const v1x = a.x - vertex.x;
  const v1y = a.y - vertex.y;
  const v2x = b.x - vertex.x;
  const v2y = b.y - vertex.y;

  const mag1 = Math.hypot(v1x, v1y);
  const mag2 = Math.hypot(v2x, v2y);
  if (mag1 === 0 || mag2 === 0) return null;

  const r = Math.min(radius, ARC_LEG_FRACTION_CAP * Math.min(mag1, mag2));
  const u1x = v1x / mag1;
  const u1y = v1y / mag1;
  const u2x = v2x / mag2;
  const u2y = v2y / mag2;

  const p1x = vertex.x + u1x * r;
  const p1y = vertex.y + u1y * r;
  const p2x = vertex.x + u2x * r;
  const p2y = vertex.y + u2y * r;

  // SVG y-down: positive cross u1 × u2 means u2 sits clockwise from u1, so
  // sweep=1 draws the short interior arc.
  const sweep = u1x * u2y - u1y * u2x > 0 ? 1 : 0;
  return `M ${p1x} ${p1y} A ${r} ${r} 0 0 ${sweep} ${p2x} ${p2y}`;
};

export const bisectorAnchor = (
  vertex: Coordinate,
  a: Coordinate,
  b: Coordinate,
  angleDeg: number,
  perpendicularOffset: number
): [number, number] | null => {
  const v1x = a.x - vertex.x;
  const v1y = a.y - vertex.y;
  const v2x = b.x - vertex.x;
  const v2y = b.y - vertex.y;

  const mag1 = Math.hypot(v1x, v1y);
  const mag2 = Math.hypot(v2x, v2y);
  if (mag1 === 0 || mag2 === 0) return null;

  const bx = v1x / mag1 + v2x / mag2;
  const by = v1y / mag1 + v2y / mag2;
  const bMag = Math.hypot(bx, by);
  if (bMag === 0) return null;

  // Place label at a constant perpendicular distance from both legs so it
  // reads as visually equidistant across corners regardless of angle width.
  const halfAngle = (angleDeg * Math.PI) / 180 / 2;
  const sinHalf = Math.sin(halfAngle);
  if (sinHalf < 1e-6) return null;

  const legCap = LEG_FRACTION_CAP * Math.min(mag1, mag2);
  const along = Math.min(perpendicularOffset / sinHalf, legCap);

  return [vertex.x + (bx / bMag) * along, vertex.y + (by / bMag) * along];
};
